import React, { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL =
  process.env.REACT_APP_API_BASE || "https://ngo-cms-backend-5oez.onrender.com/api";

export default function Home() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ New state for Mission & Vision
  const [siteContent, setSiteContent] = useState(null);

  const fallbackCampaigns = [
    {
      title: "Education Drive",
      description: "Providing learning opportunities for underprivileged children.",
      image:
        "https://images.unsplash.com/photo-1588072432836-e10032774350?auto=format&fit=crop&w=600&q=80",
      details:
        "This campaign focuses on building schools, providing books, and training teachers in rural areas.",
    },
    {
      title: "Mobile Health Units",
      description: "Bringing healthcare to rural communities.",
      image:
        "https://images.unsplash.com/photo-1588776814546-ec7d3f3f3f3d?auto=format&fit=crop&w=600&q=80",
      details:
        "Our mobile vans travel to villages offering free checkups, medicines, and health awareness programs.",
    },
    {
      title: "Skill Training",
      description: "Empowering youth and women with vocational skills.",
      image:
        "https://images.unsplash.com/photo-1509099836639-18ba106728d4?auto=format&fit=crop&w=600&q=80",
      details:
        "We provide training in tailoring, computer literacy, and other skills to help secure livelihoods.",
    },
  ];

  useEffect(() => {
    // ✅ Fetch campaigns
    axios
      .get(`${BACKEND_URL}/campaigns/`)
      .then((res) => {
        setCampaigns(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching campaigns:", err);
        setCampaigns(fallbackCampaigns);
        setError("⚠️ Unable to load live campaigns. Showing fallback data.");
        setLoading(false);
      });

    // ✅ Fetch Mission & Vision content
    axios
      .get(`${BACKEND_URL}/site-content/`)
      .then((res) => {
        if (res.data.length > 0) {
          setSiteContent(res.data[0]); // assume single record
        }
      })
      .catch((err) => {
        console.error("Error fetching site content:", err);
      });
  }, []);

  const openInfoModal = (campaign) => {
    setSelectedCampaign(campaign);
    const modalEl = document.getElementById("infoModal");
    if (modalEl && window.bootstrap) {
      const modal = new window.bootstrap.Modal(modalEl);
      modal.show();
    }
  };

  return (
    <div className="container py-4">
      {/* Hero Section */}
      <section className="text-center mb-5">
        <h1 className="display-5 fw-bold text-primary">Creating Lasting Impact</h1>
        <p className="lead text-muted">
          Education, healthcare, and livelihood opportunities for underprivileged communities.
        </p>
      </section>

      {/* Mission & Vision Banner Image */}
      {siteContent?.banner_image && (
        <section className="mb-4 text-center">
          <img
            src={siteContent.banner_image}
            alt="Community support"
            className="img-fluid rounded shadow-sm"
            style={{ maxHeight: "400px", objectFit: "cover", width: "100%" }}
            onError={(e) =>
              (e.target.src =
                "https://via.placeholder.com/1200x400?text=Mission+%26+Vision")
            }
          />
        </section>
      )}

      {/* Mission & Vision Section */}
      <section
        className="mb-5 p-4 rounded shadow-sm"
        style={{
          background: "linear-gradient(135deg, #f0f8ff, #e6f7ff)",
        }}
      >
        <div className="row text-center">
          <div className="col-md-6 mb-4 mb-md-0">
            <i className="bi bi-bullseye text-primary fs-1 mb-3"></i>
            <h3 className="fw-bold text-primary">Our Mission</h3>
            <p className="text-muted">
              {siteContent?.mission ||
                "To uplift underprivileged communities by providing access to education, healthcare, and sustainable livelihood opportunities."}
            </p>
          </div>
          <div className="col-md-6">
            <i className="bi bi-eye text-primary fs-1 mb-3"></i>
            <h3 className="fw-bold text-primary">Our Vision</h3>
            <p className="text-muted">
              {siteContent?.vision ||
                "A world where every person, regardless of background, has the opportunity to thrive."}
            </p>
          </div>
        </div>
      </section>

      {/* Impact Highlights */}
      <section className="row text-center mb-5">
        <div className="col-md-3">
          <h2 className="fw-bold text-success">3000+</h2>
          <p>Campaigns Hosted</p>
        </div>
        <div className="col-md-3">
          <h2 className="fw-bold text-info">10,000+</h2>
          <p>Children Educated</p>
        </div>
        <div className="col-md-3">
          <h2 className="fw-bold text-warning">5,000+</h2>
          <p>Patients Treated</p>
        </div>
        <div className="col-md-3">
          <h2 className="fw-bold text-danger">2,000+</h2>
          <p>Volunteers Across India</p>
        </div>
      </section>

      {/* Featured Campaigns */}
      <section>
        <h2 className="mb-4 text-center fw-bold">Featured Campaigns</h2>

        {loading ? (
          <div className="text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-2">Loading campaigns...</p>
          </div>
        ) : (
          <div className="row g-4">
            {(campaigns.length ? campaigns : fallbackCampaigns).map((c, idx) => (
              <div className="col-12 col-md-4" key={idx}>
                <div className="card h-100 shadow-sm">
                  <img
                    src={c.image}
                    alt={c.title}
                    className="card-img-top"
                    style={{ maxHeight: "200px", objectFit: "cover" }}
                    onError={(e) =>
                      (e.target.src =
                        "https://via.placeholder.com/400x200?text=Image+Unavailable")
                    }
                  />
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title fw-semibold">{c.title}</h5>
                    <p className="card-text text-muted flex-grow-1">
                      {c.description}
                    </p>
                    <button
                      className="btn btn-sm btn-outline-primary mt-auto"
                      onClick={() => openInfoModal(c)}
                    >
                      Learn More
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && <p className="text-warning text-center mt-3">{error}</p>}
      </section>

      {/* Info Modal */}
      <div
        className="modal fade"
        id="infoModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{selectedCampaign?.title}</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>
            <div className="modal-body">
              {selectedCampaign && (
                <>
                  <img
                    src={selectedCampaign.image}
                    alt={selectedCampaign.title}
                    className="img-fluid mb-3 rounded"
                  />
                  <p>
                    <strong>Description:</strong> {selectedCampaign.description}
                  </p>
                  {selectedCampaign.details && (
                    <p>
                      <strong>Details:</strong> {selectedCampaign.details}
                    </p>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}